import { Box, Button, Dialog, DialogContent, Stack } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import dayjs from "dayjs";
import React, { type FC, useEffect } from "react";
import * as yup from "yup";

import { missionRepo } from "@repositories";
import {
  type Mission,
  type TaskTypes,
  type TaskPriority,
  type TaskStatus,
} from "@shared/entities/mission/entity";

import { FormSelect } from "../../../components/form-components/form-select-input/FormSelect";
import { FormTextField } from "../../../components/form-components/form-text-field/FormTextField";
import { useAlert } from "../../../contexts/AlertProvider";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { useUser } from "../../../contexts/UserProvider";
import { useOrganizationManagerOptions } from "../../../queries/users.query";
import FormProvider, { useForm } from "../../../helpers/forms/FormProvider";
import { provide } from "../../../helpers/wrap-and-provide";

import MissionConnectionField from "./MissionConnectionField";
import "./MissionDialog.scss";

export const taskPriorityOptions = ["רגילה", "גבוהה", "דחופה", ""] as const;
export const taskStatusOptions = ["לא בוצעה", "בתהליך", "בוצעה", ""] as const;

interface MissionDialogProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mission?: Mission;
  onSuccess: () => void;
}

const validationSchema = yup.object().shape({
  description: yup
    .string()
    .min(5, "תיאור המשימה חייב להכיל לפחות 5 תווים")
    .required("שדה חובה"),
  date: yup.date().required("שדה חובה"),
  priority: yup
    .string()
    .oneOf(taskPriorityOptions, "ערך לא חוקי")
    .required("שדה חובה"),
  status: yup
    .string()
    .oneOf(taskStatusOptions, "ערך לא חוקי")
    .required("שדה חובה"),
});

const DEFAULT_VALUES: Partial<Mission> & { isAidRelated?: boolean; } = {
  description: "",
  date: new Date(),
  priority: "רגילה" as TaskPriority,
  status: "לא בוצעה" as TaskStatus,
  associationId: "",
  groupId: "",
  opportunityId: "",
  teamId: "",
  assignedManagerId: "",
  comments: "",
  types: [] as TaskTypes[],
};

export const MissionDialog: FC<MissionDialogProps> = ({
  isOpen,
  setIsOpen,
  mission,
  onSuccess,
}) => {
  const { setAlertText, setSeverity } = useAlert();
  const { resetForm, values, isValid, setValues, setFieldValue } = useForm<
    Partial<Mission> & { isAidRelated?: boolean; }
  >();
  const { currOrganization } = useOrganization();
  const { data: organizationManagers = [] } = useOrganizationManagerOptions();
  const [isLoading, setIsLoading] = React.useState(false);
  const isEditMode = Boolean(mission);
  const { currentUser } = useUser();

  useEffect(() => {
    if (mission && isOpen) {
      const initialValues = {
        description: mission.description,
        date: mission.date,
        priority: mission.priority,
        status: mission.status || "לא בוצעה",
        associationId: mission.associationId || "",
        groupId: mission.groupId || "",
        opportunityId: mission.opportunityId || "",
        teamId: mission.teamId || "",
        assignedManagerId: mission.assignedManagerId || "",
        comments: mission.comments || "",
        types: mission.types || [],
      };

      // Only set values if they've actually changed
      if (JSON.stringify(values) !== JSON.stringify(initialValues)) {
        setValues(initialValues);
      }
    }
  }, [mission, isOpen]); // Remove setValues from dependencies

  const saveMission = async () => {
    if (!isValid || isLoading) return;
    const {
      associationId,
      teamId,
      groupId,
      organizationId,
      opportunityId,
      assignedManagerId,
      creatorManagerId,
      isAidRelated,
      ...restValues
    } = values;

    try {
      setIsLoading(true);
      const types: TaskTypes[] = [];
      if (teamId) types.push("TEAM");
      if (groupId) types.push("GROUP");
      if (associationId && isAidRelated) types.push("AID_RELATED");
      if (associationId && !isAidRelated) types.push("USER");
      if (opportunityId) types.push("OPPORTUNITY");

      await missionRepo.save({
        ...(mission ? { id: mission.id } : {}),
        ...restValues,
        types,
        creatorManagerId: isEditMode
          ? mission?.creatorManagerId
          : currentUser?.id,
        organizationId: organizationId || currOrganization?.id,
        associationId,
        teamId,
        groupId,
        opportunityId,
        assignedManagerId,
      });

      setAlertText(isEditMode ? "המטלה עודכנה בהצלחה" : "המטלה נוצרה בהצלחה");
      setSeverity("success");
      resetForm();
      onSuccess();
      setIsOpen(false);
    } catch (error) {
      setAlertText(
        isEditMode ? "אירעה שגיאה בעדכון המטלה" : "אירעה שגיאה ביצירת המטלה",
      );
      setSeverity("error");
      console.error("Error saving mission:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      className="mission-dialog"
      open={isOpen}
      onClose={() => {
        setIsOpen(false);
        resetForm();
      }}
      maxWidth="md"
      fullWidth
    >
      <Box className="mission-dialog__header">
        <h2>{isEditMode ? "עריכת מטלה" : "יצירת מטלה חדשה"}</h2>
      </Box>

      <DialogContent className="mission-dialog__content">
        <Stack spacing={2}>
          <FormTextField
            name="description"
            label="תיאור"
            multiline
            rows={3}
            fullWidth
          />

          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={
              heIL.components.MuiLocalizationProvider.defaultProps.localeText
            }
            adapterLocale="he"
          >
            <DateTimePicker
              label="תאריך ושעה"
              format="DD/MM/YYYY HH:mm"
              value={dayjs(values?.date)}
              onChange={(newValue) => {
                if (newValue) {
                  setFieldValue("date", newValue.toDate());
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  className: "field",
                },
              }}
            />
          </LocalizationProvider>

          <FormSelect
            name="priority"
            label="דחיפות"
            options={taskPriorityOptions.filter(Boolean)}
          />

          <FormSelect
            name="status"
            label="סטטוס"
            options={taskStatusOptions.filter(Boolean)}
          />

          <FormSelect
            name="assignedManagerId"
            label="מנהל אחראי"
            options={
              organizationManagers.filter(Boolean).map((manager) => ({
                content: manager.name,
                value: manager.id,
              }))
            } // Allow empty value
            value={values.assignedManagerId || ""} // Ensure controlled behavior
          />

          <MissionConnectionField
            mission={mission}
            onChange={(connections) => {
              setValues((prev) => ({
                ...prev,
                teamId: connections.teamId || "",
                groupId: connections.groupId || "",
                associationId: connections.associationId || "",
                opportunityId: connections.opportunityId || "",
                isAidRelated: !!connections.isAidRelated,
              }));
            }}
          />

          <FormTextField
            name="comments"
            label="הערות"
            multiline
            rows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <Box className="mission-dialog__footer">
        <Button
          onClick={() => {
            setIsOpen(false);
            resetForm();
          }}
          variant="outlined"
        >
          ביטול
        </Button>
        <Button
          variant="contained"
          onClick={saveMission}
          disabled={!isValid || isLoading}
        >
          {isEditMode ? "שמירה" : "יצירה"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default provide([
  FormProvider<Partial<Mission>>,
  {
    initialValues: DEFAULT_VALUES,
    validationSchema,
    onSubmit: () => { },
  },
])(MissionDialog);
